import {
  base_colorContentBorder,
  base_colorContentBorderSecondary,
  base_colorSurfaceMuted,
  tag_colorTagPendingText,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { cornerRadius, spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  font_lg,
  font_md,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

/**
 * Styles for the Bill Pay "Bank Account" settings surface — the two-card
 * KYB-gated layout (Business Verification + Bank Account). Mirrors Richa's
 * prototype (extend-design-render/invoice-pay-prototype, bank-account-v2):
 * two equal (50/50) content cards, each = optional top banner + padded body
 * + optional footer bar. Tokens only.
 */
/**
 * The prototype strips the ADS AlertPanel's rounded full-box border down to a
 * single bottom divider (`rounded-none border-x-0 border-t-0`) — a flush strip at
 * the top of the card, not a rounded bordered box. ext-ui has no flush
 * "banner-in-a-card" variant, so we replicate that reshape. Bottom border keeps
 * the variant color (yellow for warning); the locked banner recolors it grey.
 * `!important` beats the generated AlertPanel base border/radius.
 */
const flushBanner = css`
  border-radius: 0 !important;
  border-width: 0 0 1px 0 !important;
`

/**
 * Shared banner-text base: column layout + the 14px/18px size. Per-variant text
 * COLOR is applied by `bannerWarning` / `bannerLocked` (which compose this), not
 * here — because ext-ui's AlertPanel forces its `.subtitle` grey regardless of
 * variant, and with no visible title slot we render BOTH title and description
 * inside that subtitle, so the color has to be set explicitly on our own spans
 * (verified in-browser: without it the computed color was grey #697080, not the
 * variant amber).
 */
const bannerContent = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(1)};
  /* ext-ui AlertPanel's .subtitle element is 10px, but its panel root — and
     the prototype's alert-panel — is 14px/18px. Since ext-ui has no visible
     title slot we render BOTH title and description inside the subtitle, so
     without this the description inherits the 10px subtitle size. font_md
     (= the panel root's own 14px/18px) puts the banner text back on spec. */
  ${font_md}
`

/* Banner title: medium weight at the same 14px/18px (font_md is inherited from
   bannerContent). Overrides ext-ui's .font-bold utility, which is 16px. */
const bannerTitle = css`
  font-family: ${font_family_weight_medium};
`

const styles = {
  row: css`
    display: flex;
    align-items: flex-start;
    gap: ${spacing(5)};
    width: 100%;
  `,
  col: css`
    flex: 1;
    min-width: 0;
  `,
  card: css`
    overflow: hidden;
    width: 100%;
    /* Prototype is rounded-[8px]; the shared Box primitive defaults to the
       legacy 10px. Override with ext-ui's own cornerRadius token (= 8px) so
       the card matches the design's radius. */
    border-radius: ${cornerRadius};
  `,
  bannerWarning: css`
    ${bannerContent}
    color: ${tag_colorTagPendingText};
  `,
  bannerLocked: css`
    ${bannerContent}
    color: ${text_colorTextSecondary};
  `,
  bannerTitle,
  // Locked-card footer label. The prototype renders "Locked" as a 14px/medium
  // secondary-grey span (matching the "Start verification" button beside it on
  // the sibling card); a bare .text-secondary span would inherit the app's
  // default 16px/regular body text instead.
  lockedFooter: css`
    ${font_md}
    font-family: ${font_family_weight_medium};
    color: ${text_colorTextSecondary};
  `,
  lockedTitle: css`
    ${bannerTitle}
    color: ${text_colorTextPrimary};
  `,
  // Warning/KYB banner: flush strip; bottom divider keeps the variant yellow.
  bannerFlush: flushBanner,
  /**
   * Locked banner. ext-ui AlertPanel has no grey/"locked" variant, so the
   * "default" panel renders soft-blue with a full rounded box border — wrong for a
   * locked/disabled state. We approximate the prototype's `ContentCard
   * variant="locked"` (a muted grey strip) with a flush bottom-only divider.
   *
   * Fill uses grey-450 (`base_colorContentBorder`, #DADBE0) — the prototype's
   * literal grey — per design's preference for the darker, more-visible strip.
   * KNOWN TRADEOFF: grey-450 against the secondary-grey description text is
   * 3.59:1, below WCAG AA (4.5:1). Accepted for now (design call); to be resolved
   * properly via a real DS locked/muted *surface* token + flush AlertPanel
   * variant (Richa list), which can pair with AA-passing text. `!important` beats
   * the generated panel styles.
   */
  lockedBanner: css`
    ${flushBanner}
    background: ${base_colorContentBorder} !important;
    border-bottom-color: ${base_colorContentBorder} !important;
  `,
  body: css`
    display: flex;
    flex-direction: column;
    gap: ${spacing(5)};
    padding: ${spacing(10)};
  `,
  bodyText: css`
    ${font_lg}
  `,
  field: css`
    display: flex;
    flex-direction: column;
    gap: ${spacing(1)};
  `,
  details: css`
    display: flex;
    flex-direction: column;
    gap: ${spacing(6)};
  `,
  footer: css`
    display: flex;
    align-items: center;
    border-top: 1px solid ${base_colorContentBorderSecondary};
    background: ${base_colorSurfaceMuted};
    padding: ${spacing(4)} ${spacing(10)};
  `,
}

export default styles
